/**
 * Keeps GoFundMe's abandon-cart tray off the page for good.
 *
 * Their SDK injects `<cl-nudge-tray>` as a direct child of `<body>` — the
 * "Finish your $250 gift / Give Now" bar. `donate.scss` hides it with
 * `display: none !important`, and that is what does the work in every situation
 * observed: the tray stays hidden when their SDK flips `open="true"`, and an
 * inline `display: block` cannot override it either.
 *
 * There is exactly one thing a stylesheet cannot beat: an inline style that is
 * itself `!important`, which wins on cascade order no matter what we write. That
 * is the hole this module closes, and the only one.
 *
 * WHY NOT JUST REMOVE THE ELEMENT
 *
 * Their SDK keeps a reference to it and reads it back. Removing it risks
 * throwing inside their bundle, which would take the checkout down with it —
 * a much worse outcome than a visible tray. Neutralising it in place is the
 * conservative move: their code still finds the element it expects.
 *
 * The observer watches `style` only on trays it has already found, plus
 * `childList` on `<body>` for one appearing later, and writes nothing unless the
 * element is actually visible — so our own write cannot feed itself a loop.
 */

const TRAY = "cl-nudge-tray";

/** Re-asserts the hide, but only when something has actually undone it. */
function hide(tray: HTMLElement): void {
  if (getComputedStyle(tray).display === "none") return;
  tray.style.setProperty("display", "none", "important");
}

export function suppressNudgeTray(): () => void {
  if (typeof MutationObserver === "undefined") return () => {};

  const watched = new WeakSet<HTMLElement>();

  const watch = (tray: HTMLElement): void => {
    hide(tray);
    if (watched.has(tray)) return;
    watched.add(tray);
    styles.observe(tray, { attributes: true, attributeFilter: ["style"] });
  };

  // Separate observer so the attribute filter stays narrow: watching attributes
  // on <body> as well would fire on every unrelated change the vendors make.
  const styles = new MutationObserver((records) => {
    for (const record of records) hide(record.target as HTMLElement);
  });

  const tree = new MutationObserver(() => {
    for (const tray of document.querySelectorAll<HTMLElement>(TRAY)) watch(tray);
  });

  for (const tray of document.querySelectorAll<HTMLElement>(TRAY)) watch(tray);
  tree.observe(document.body, { childList: true });

  return () => {
    tree.disconnect();
    styles.disconnect();
  };
}
